import { useEffect, useState } from 'react';
import {
  onSnapshot,
  query,
  where,
  orderBy,
  collection,
  doc,
  type Query,
  type DocumentData,
} from 'firebase/firestore';
import type {
  ProjectDoc,
  TaskDoc,
  RunDoc,
  ExecutionDoc,
  BacklogCacheDoc,
  ActivityLogDoc,
  AssigneeDoc,
  AssigneeUserMappingDoc,
} from '@mct/shared';
import { getDb } from '../lib/firebase';
import { useAuthState } from './useAuth';

function useCollection<T>(
  qOrNull: Query<DocumentData> | null,
): { data: T[]; loading: boolean; error: Error | null } {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!qOrNull) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      qOrNull,
      (snap) => {
        setData(snap.docs.map((d) => d.data() as T));
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [qOrNull]);

  return { data, loading, error };
}

function useDocument<T>(
  path: string[] | null,
): { data: T | null; loading: boolean; error: Error | null } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!path || path.length < 1) {
      setData(null);
      setLoading(false);
      return;
    }
    const db = getDb();
    const [first, ...rest] = path;
    const ref = doc(db, first!, ...rest);
    setLoading(true);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setData(snap.exists() ? (snap.data() as T) : null);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [path?.join('/')]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, error };
}

export function useMyProjects(uid: string | null) {
  const [q, setQ] = useState<Query<DocumentData> | null>(null);
  useEffect(() => {
    if (!uid) return setQ(null);
    setQ(
      query(
        collection(getDb(), 'projects'),
        where('memberUserIds', 'array-contains', uid),
        orderBy('updatedAt', 'desc'),
      ),
    );
  }, [uid]);
  return useCollection<ProjectDoc>(q);
}

export function useProject(projectId: string | null) {
  return useDocument<ProjectDoc>(
    projectId ? ['projects', projectId] : null,
  );
}

export function useTasks(projectId: string | null) {
  const { user } = useAuthState();
  const uid = user?.uid ?? null;
  const [q, setQ] = useState<Query<DocumentData> | null>(null);
  useEffect(() => {
    if (!projectId || !uid) return setQ(null);
    setQ(
      query(
        collection(getDb(), 'projects', projectId, 'tasks'),
        where('memberUserIds', 'array-contains', uid),
        orderBy('createdAt', 'asc'),
      ),
    );
  }, [projectId, uid]);
  return useCollection<TaskDoc>(q);
}

export function useRuns(projectId: string | null) {
  const { user } = useAuthState();
  const uid = user?.uid ?? null;
  const [q, setQ] = useState<Query<DocumentData> | null>(null);
  useEffect(() => {
    if (!projectId || !uid) return setQ(null);
    setQ(
      query(
        collection(getDb(), 'projects', projectId, 'runs'),
        where('memberUserIds', 'array-contains', uid),
        orderBy('order', 'asc'),
      ),
    );
  }, [projectId, uid]);
  return useCollection<RunDoc>(q);
}

export function useRun(projectId: string | null, runId: string | null) {
  return useDocument<RunDoc>(
    projectId && runId ? ['projects', projectId, 'runs', runId] : null,
  );
}

export function useExecutionsForRun(
  projectId: string | null,
  runId: string | null,
) {
  const { user } = useAuthState();
  const uid = user?.uid ?? null;
  const [q, setQ] = useState<Query<DocumentData> | null>(null);
  useEffect(() => {
    if (!projectId || !runId || !uid) return setQ(null);
    setQ(
      query(
        collection(getDb(), 'projects', projectId, 'executions'),
        where('memberUserIds', 'array-contains', uid),
        where('runId', '==', runId),
      ),
    );
  }, [projectId, runId, uid]);
  return useCollection<ExecutionDoc>(q);
}

export function useTaskHistory(
  projectId: string | null,
  taskId: string | null,
) {
  const { user } = useAuthState();
  const uid = user?.uid ?? null;
  const [q, setQ] = useState<Query<DocumentData> | null>(null);
  useEffect(() => {
    if (!projectId || !taskId || !uid) return setQ(null);
    setQ(
      query(
        collection(getDb(), 'projects', projectId, 'executions'),
        where('memberUserIds', 'array-contains', uid),
        where('taskId', '==', taskId),
        orderBy('runOrder', 'desc'),
      ),
    );
  }, [projectId, taskId, uid]);
  return useCollection<ExecutionDoc>(q);
}

export function useBacklogCache(projectId: string | null) {
  const [q, setQ] = useState<Query<DocumentData> | null>(null);
  useEffect(() => {
    if (!projectId) return setQ(null);
    setQ(collection(getDb(), 'projects', projectId, 'backlogCache') as Query<DocumentData>);
  }, [projectId]);
  return useCollection<BacklogCacheDoc>(q);
}

/**
 * Stream the project's assignee list — the unified source of "who can
 * own work". WBS rows, migration tasks, and the settings mapping page
 * all read from this one query.
 *
 * Sorted by `name` for stable display ordering; the rules-side
 * `memberUserIds` filter mirrors the project's other subcollections so
 * non-members get nothing.
 */
export function useAssignees(projectId: string | null) {
  const { user } = useAuthState();
  const uid = user?.uid ?? null;
  const [q, setQ] = useState<Query<DocumentData> | null>(null);
  useEffect(() => {
    if (!projectId || !uid) return setQ(null);
    setQ(
      query(
        collection(getDb(), 'projects', projectId, 'assignees'),
        where('memberUserIds', 'array-contains', uid),
        orderBy('name', 'asc'),
      ),
    );
  }, [projectId, uid]);
  return useCollection<AssigneeDoc>(q);
}

/**
 * Stream the optional 1:1 mapping table (`assigneeId` -> Firebase UID).
 * The settings page renders + edits these; runtime "my work" filters
 * read them to resolve the current user's `assigneeId`.
 */
export function useAssigneeUserMappings(projectId: string | null) {
  const { user } = useAuthState();
  const uid = user?.uid ?? null;
  const [q, setQ] = useState<Query<DocumentData> | null>(null);
  useEffect(() => {
    if (!projectId || !uid) return setQ(null);
    setQ(
      query(
        collection(getDb(), 'projects', projectId, 'assigneeUserMap'),
        where('memberUserIds', 'array-contains', uid),
      ),
    );
  }, [projectId, uid]);
  return useCollection<AssigneeUserMappingDoc>(q);
}

export function useActivityLogs(projectId: string | null, max = 100) {
  const [q, setQ] = useState<Query<DocumentData> | null>(null);
  useEffect(() => {
    if (!projectId) return setQ(null);
    setQ(
      query(
        collection(getDb(), 'projects', projectId, 'activityLogs'),
        orderBy('createdAt', 'desc'),
      ),
    );
  }, [projectId]);
  const result = useCollection<ActivityLogDoc>(q);
  return { ...result, data: result.data.slice(0, max) };
}
